import { pLimit } from './p-limit';

const fetchWithCorsCatch = async (src: string, init?: RequestInit) => {
  for (let attempt = 0; ; attempt++) {
    try {
      const response = await fetch(src, {
        mode: 'cors',
        // An audio preview may have populated a partial/no-CORS browser cache entry.
        // Waveform decoding needs fresh readable bytes, not that playback response.
        cache: 'no-store',
        credentials: 'omit',
        referrerPolicy: 'no-referrer-when-downgrade',
        ...init,
      });
      return response;
    } catch (err) {
      const error = err as Error;
      if (
        // Chrome 브라우저
        error.message.includes('Failed to fetch') ||
        // Safari 브라우저
        error.message.includes('Load failed') ||
        // Firefox 브라우저
        error.message.includes('NetworkError when attempting to fetch resource')
      ) {
        // Retry only transport failures; permission/HTTP and decoding failures remain visible.
        if (attempt < 3) {
          await new Promise(resolve => setTimeout(resolve, [250, 1000, 2000][attempt]));
          continue;
        }
        throw new TypeError(
          `Failed to read from ${src}: ${error.message}. Does the resource support CORS?`,
        );
      }

      throw err;
    }
  }
};
const isRemoteAsset = (asset: string) =>
  !asset.startsWith(window.origin) && !asset.startsWith('data');

type MediaUtilsAudioData = {
  channelWaveforms: Float32Array[];
  sampleRate: number;
  durationInSeconds: number;
  numberOfChannels: number;
  resultId: string;
  isRemote: boolean;
};

export type ImageDimensions = { width: number; height: number };

const metadataCache: { [key: string]: MediaUtilsAudioData } = {};

const limit = pLimit(3);

const fn = async (src: string): Promise<MediaUtilsAudioData> => {
  if (metadataCache[src]) {
    return metadataCache[src];
  }

  if (typeof document === 'undefined') {
    throw new Error('getAudioData() is only available in the browser.');
  }

  const response = await fetchWithCorsCatch(src);
  if (!response.ok) throw new Error(`Audio source returned HTTP ${response.status}`);
  const arrayBuffer = await response.arrayBuffer();
  const audioContext = new AudioContext({ sampleRate: 48000 });
  try {
  const wave = await audioContext.decodeAudioData(arrayBuffer);

  const channelWaveforms = new Array(wave.numberOfChannels)
    .fill(true)
    .map((_, channel) => {
      return wave.getChannelData(channel);
    });

  const metadata: MediaUtilsAudioData = {
    channelWaveforms,
    sampleRate: wave.sampleRate,
    durationInSeconds: wave.duration,
    numberOfChannels: wave.numberOfChannels,
    resultId: String(Math.random()),
    isRemote: isRemoteAsset(src),
  };
  metadataCache[src] = metadata;
  return metadata;
  } finally {
    await audioContext.close().catch(() => {});
  }
};

/*
 * @description audio 또는 video src를 받아서 로드하고 지정된 source의 data와 metadata를 반환합니다.
 * @see [Documentation](https://remotion.dev/docs/get-audio-data)
 */
const inFlight = new Map<string, Promise<MediaUtilsAudioData>>();
export const getAudioData = (src: string) => {
  const pending = inFlight.get(src);
  if (pending) return pending;
  const request = limit(fn, src).finally(() => { inFlight.delete(src); });
  inFlight.set(src, request);
  return request;
};
